import { ColdTransferToQueue20Regular } from "@/common/icons";
import {
	InteractionType,
	TransferChannelType,
	TransferType,
} from "@/common/types";
import { InteractionButton } from "@/components/atoms/interactionButton";
import { PopoverView } from "@/components/atoms/popover";
import { InteractionTransferToAgentPopover } from "@/components/organisms/InteractionTransferToAgent";
import { InteractionTransferIvrPopover } from "@/components/organisms/interactionTransferToIvr/InteractionTransferIvrPopover";
import { useAgentInteractionState } from "@/hooks/agent";
import { InteractionDto } from "@/repository/models";
import { tokens } from "@fluentui/react-components";
import { ArrowForward20Regular } from "@fluentui/react-icons";
import { useCallback, useState } from "react";
import { useActiveInteractionsActions } from "../../../hooks/allInteractions/useActiveInteractionActions";
import { useIntl } from "react-intl";
import { useQueuesStore, QueuesState } from "@/common/stores/nonpersistingStores/queues";
import { useActiveInteractions } from "../../../hooks/allInteractions/useActiveInteractions";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";

export const useTransferActions = (onAlreadyToggledItem?: () => void) => {
	const [openPopovers, setOpenPopovers] = useState<{
		[key: string]: TransferType | null;
	}>({});
	const { agentHasCallInProgressOrTextInOffering } = useAgentInteractionState();

	const { isMonitoringDisabled, shouldHideMonitoringButtons } =
		useActiveInteractionsActions();
	const queues = useQueuesStore((state: QueuesState) => state.queues);
	const { activeInteractions } = useActiveInteractions();

	const intl = useIntl();

	const getAgentTransferType = (type: InteractionType) => {
		switch (type) {
			case InteractionType.Email:
				return TransferType.EmailAgentTransfer;
			case InteractionType.WhatsApp:
				return TransferType.WhatsAppAgentTransfer;
			case InteractionType.Webchat:
				return TransferType.WebchatAgentTransfer;
			case InteractionType.Voicemail:
				return TransferType.VoicemailTransferToAgent;
			default:
				return TransferType.EmailAgentTransfer;
		}
	};

	const getTransferToQueueType = (type: InteractionType) => {
		switch (type) {
			case InteractionType.Email:
				return TransferType.EmailAgentTransferToQueue;
			case InteractionType.WhatsApp:
				return TransferType.WhatsAppTransferToQueue;
			case InteractionType.Webchat:
				return TransferType.WebchatTransferToQueue;
			case InteractionType.Voicemail:
				return TransferType.VoicemailTransferToQueue;
			default:
				return TransferType.EmailAgentTransferToQueue;
		}
	};

	const getTransferChannelType = (type: InteractionType) => {
		switch (type) {
			case InteractionType.Email:
				return TransferChannelType.Email;
			case InteractionType.WhatsApp:
				return TransferChannelType.WhatsApp;
			case InteractionType.Webchat:
				return TransferChannelType.WebChat;
			case InteractionType.Voicemail:
				return TransferChannelType.Voicemail;
			default:
				return TransferChannelType.Email;
		}
	};

	const toggleTransferPopoverOpening = useCallback(
		(interactionId: string, type: TransferType) => {
			setOpenPopovers((prevState) => {
				const isAlreadyToggled = prevState[interactionId] === type;
				if (isAlreadyToggled) {
					if (onAlreadyToggledItem) {
						onAlreadyToggledItem();
					}
				}

				return {
					...prevState,
					[interactionId]: isAlreadyToggled ? null : type,
				};
			});
		},
		[]
	);

	const isSupervisorOnQueue = useCallback(
		(queueId: string) => queues.some((q) => q.id === queueId && q.isSupervisor),
		[queues]
	);

	const getTextColdTransferButtons = useCallback(
		(item: InteractionDto) => {
			const isMessaging =
				item.type === InteractionType.Email ||
				item.type === InteractionType.WhatsApp ||
				item.type === InteractionType.Webchat;

			const isVoicemail = item.type === InteractionType.Voicemail;

			if (isVoicemail) {
				if (!isSupervisorOnQueue(item.queueId)) {
					return <></>;
				}
				const userId = Lockr.get(StorageKeys.UserId);
				if (item.agentId && item.agentId === userId) {
					return <></>;
				}
			} else if (!isMessaging || isMonitoringDisabled(item) || shouldHideMonitoringButtons(item)) {
				return <></>;
			}
			const openPopoverType = openPopovers[item.id];

			const agentTransferType = getAgentTransferType(item.type);
			const transferToQueueType = getTransferToQueueType(item.type);
			const channelType = getTransferChannelType(item.type);

			return (
				<>
					<PopoverView
						trigger={
							<InteractionButton
								tooltipMessage={intl.formatMessage({
									id: item.hasActiveVideoCall
										? "Interaction.Buttons.TransferDisabledDuringVideoCall"
										: "Interaction.Buttons.ColdTransfer",
								})}
								onClickCallBack={() => {
									toggleTransferPopoverOpening(item.id, agentTransferType);
								}}
								icon={
									<ArrowForward20Regular
										primaryFill={tokens.colorNeutralStrokeOnBrand2}
									/>
								}
								disabled={item.hasActiveVideoCall ?? false}
							/>
						}
						content={
							<InteractionTransferToAgentPopover
								closePopover={() =>
									toggleTransferPopoverOpening(item.id, agentTransferType)
								}
								channelType={channelType}
								messagingId={item.id}
								onTransferCallback={() =>
									toggleTransferPopoverOpening(item.id, agentTransferType)
								}
							/>
						}
						positioning={{ position: "below" }}
						open={openPopoverType === agentTransferType}
					/>
					<PopoverView
						trigger={
							<InteractionButton
								tooltipMessage={intl.formatMessage({
									id: item.hasActiveVideoCall
										? "Interaction.Buttons.TransferDisabledDuringVideoCall"
										: "Interaction.Buttons.ColdTransferToOnlyQueue",
								})}
								onClickCallBack={() =>
									toggleTransferPopoverOpening(item.id, transferToQueueType)
								}
								icon={
									<ColdTransferToQueue20Regular
										primaryFill={tokens.colorNeutralStrokeOnBrand2}
									/>
								}
								disabled={item.hasActiveVideoCall ?? false}
							/>
						}
						content={
							<InteractionTransferIvrPopover
								closePopover={() =>
									toggleTransferPopoverOpening(item.id, transferToQueueType)
								}
								channelType={channelType}
								messagingId={item.id}
								onTransferCallback={() =>
									toggleTransferPopoverOpening(item.id, transferToQueueType)
								}
							/>
						}
						positioning={{ position: "below" }}
						open={openPopoverType === transferToQueueType}
					/>
				</>
			);
		},
		[
			openPopovers,
			agentHasCallInProgressOrTextInOffering,
			queues,
			activeInteractions,
			isSupervisorOnQueue,
		]
	);

	return { openPopovers, getTextColdTransferButtons };
};
